import React from 'react';
import { Loader2, CloudUpload, BrainCircuit, CheckCircle2, Music, Sparkles } from 'lucide-react';
import { ProcessStatus } from '../types';

interface ProgressBarProps {
  extractionProgress: number;
  generationProgress: number;
  status: ProcessStatus;
}

const ProgressBar: React.FC<ProgressBarProps> = ({ extractionProgress, generationProgress, status }) => {
  const isExtracting = status === ProcessStatus.EXTRACTING || status === ProcessStatus.UPLOADING;
  const isAnalyzing = status === ProcessStatus.ANALYZING;
  const isCompleted = status === ProcessStatus.COMPLETED;

  const renderSingleBar = (label: string, icon: React.ReactNode, progress: number, isActive: boolean, isDone: boolean) => {
    return (
      <div className="w-full mb-4 last:mb-0">
          <div className="flex justify-between items-center mb-1.5">
             <div className="flex items-center gap-2">
                 <div className={`p-1.5 rounded-md ${isDone ? 'bg-green-500/20' : isActive ? 'bg-blue-500/20' : 'bg-gray-800'}`}>
                    {isDone ? <CheckCircle2 className="w-3.5 h-3.5 text-green-400" /> : icon}
                 </div>
                 <span className={`text-xs font-bold uppercase tracking-wider ${isActive || isDone ? 'text-gray-200' : 'text-gray-600'}`}>
                    {label}
                 </span>
             </div>
             <span className="text-xs font-mono text-gray-400">{Math.round(progress)}%</span>
          </div>
          
          <div className="h-2 w-full bg-gray-800 rounded-full overflow-hidden border border-white/5 relative">
             <div 
                className={`h-full relative transition-all duration-300 ease-out
                  ${isDone ? 'bg-green-500' : isActive ? 'bg-blue-500' : 'bg-gray-700'}
                `}
                style={{ width: `${progress}%` }}
             >
                {isActive && (
                    <div className="absolute top-0 right-0 bottom-0 w-10 bg-gradient-to-r from-transparent via-white/40 to-transparent skew-x-[-20deg] animate-[shimmer_1s_infinite]"></div>
                )}
             </div>
          </div>
      </div>
    );
  };

  return (
    <div className="w-full bg-gray-900/80 border border-white/10 p-5 rounded-2xl shadow-xl backdrop-blur-md">
       
       {/* Bar 1: Audio Extraction */}
       {renderSingleBar(
         "Audio Extraction & Upload", 
         <Music className={`w-3.5 h-3.5 ${isExtracting ? 'text-blue-400 animate-pulse' : 'text-gray-500'}`} />, 
         extractionProgress, 
         isExtracting, 
         (status === ProcessStatus.ANALYZING || isCompleted)
       )}

       {/* Bar 2: AI Generation */}
       {renderSingleBar(
         "AI Subtitle Generation", 
         <BrainCircuit className={`w-3.5 h-3.5 ${isAnalyzing ? 'text-purple-400 animate-pulse' : 'text-gray-500'}`} />, 
         generationProgress, 
         isAnalyzing, 
         isCompleted
       )}

       {isCompleted && (
           <div className="mt-4 flex items-center justify-center gap-2 text-green-400 text-sm font-bold animate-in fade-in slide-in-from-bottom-2">
               <Sparkles className="w-4 h-4" /> Process Complete!
           </div>
       )}
    </div>
  );
};

export default ProgressBar;
